// src/pages/merchant-pages/CreateInvoiceOfMultiContsPage.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Assets
import addIcon from "../../assets/add_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";

// Styles
import classes from "../../components/Modal.module.css";

// Utils
import { DataContext } from "../../utils/DataContext";
import useLanguage from "../../utils/useLanguage";
import useRandomIcon from "../../utils/useRandomIcon";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import { onlyNums } from "../../utils/OnlyNums";
import { shouldCloseModal } from "../../utils/shouldCloseModal";
import { UserAuth } from "../../utils/AuthContext";

// Components
import ShippedContainerCard from "../../components/ShippedContainerCard";
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import PDFUploader from "../../components/PDFUploader";
import InformationBox from "../../components/InformationBox";
import HotDropdown from "../../components/HotDropdown";
import AreYouSureModal from "../../components/AreYouSureModal";

// Page sections
import AddShippedContainerModal from "./AddShippedContainerModal";

const CreateInvoiceOfMultiContsPage = ({ setShowInputFields }) => {
  const [inputs, setInputs] = useState([]);

  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();

  const [showAddShippedContainerModal, setShowAddShippedContainerModal] =
    useState(false);

  // Create container modal state
  const [showAreYouSure, setShowAreYouSure] = useState(false);

  // Create container modal
  const [areYouSureDelete, setAreYouSureDelete] = useState(false);
  const [containerToDelete, setContainerToDelete] = useState(null);

  const [currentContainer, setCurrentContainer] = useState(null);
  // Handle opening the modal for a specific container
  const openModal = (index) => {
    setCurrentContainer(index);
    setShowAddShippedContainerModal(true);
  };

  const [loadingModal, setLoadingModal] = useState(false);
  const {
    suppliers,
    getSuppliers,
    selectedSupplier,
    error,
    setError,
    setShippedContainers,
    selectedCustomer,
    customers,
    handleSelectSupplier,
    handleSelectCustomer,
    subCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
    getSubCompanies,
    getCustomers,
  } = useContext(DataContext);

  const { userData } = useContext(UserAuth);
  // const [selectedCustomer, setSelectedCustomer] = useState({});

  const getRandomIcon = useRandomIcon();

  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const pdfUploaderRef = useRef(null);

  const [invoice, setInvoice] = useState({
    invoiceNumber: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    date: new Date(),
    received: 0,
    left: 0,
    notes: "",
    currencyRate: 0,
  });

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setError("");
    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    const parsedValue = value;

    setInvoice((prev) => {
      // Extract all relevant numbers from the previous state
      const {
        amountForSupplier,
        abstractAmountForCustomer,
        currencyRate,
        received,
      } = prev;

      let updatedValues = { [name]: parsedValue };

      // Define dependencies and update accordingly
      if (name === "amountForSupplier") {
        updatedValues.abstractAmountForCustomer =
          parsedValue * (currencyRate || 1);
        updatedValues.received = 0;
        updatedValues.left = 0;
      } else if (name === "currencyRate") {
        updatedValues.abstractAmountForCustomer =
          (amountForSupplier || 0) * parsedValue;
        updatedValues.received = 0;
      }
      // If the user type that value manually, empty the currencyRate value
      else if (name === "abstractAmountForCustomer") {
        updatedValues.currencyRate = 0;
        updatedValues.received = 0;
      }

      // Prevent received from exceeding abstractAmountForCustomer
      if (name === "received") {
        updatedValues.received = parsedValue;
        updatedValues.left = abstractAmountForCustomer - parsedValue || 0;
      } else {
        updatedValues.left =
          updatedValues.abstractAmountForCustomer ??
          Math.min(
            abstractAmountForCustomer - received,
            abstractAmountForCustomer
          );
      }

      return {
        ...prev,
        ...updatedValues,
      };
    });
  };

  const handleChildUploadDocs = (multiContainersInvoiceId) => {
    if (pdfUploaderRef.current) {
      pdfUploaderRef.current.handleUploadDocs(multiContainersInvoiceId);
    }
  };

  const handleAttemptDeleteContainer = (e, index) => {
    e.stopPropagation();
    setContainerToDelete(index);
    setAreYouSureDelete(true);
  };

  const handleDeleteContainer = (containerIndex) => {
    const filteredInputs = inputs?.filter(
      (input, index) => index !== containerIndex
    );

    setInputs(filteredInputs);
    setAreYouSureDelete(false);
    setContainerToDelete(null);
  };

  const handleAddRow = async () => {
    try {
      const token = userData?.token;
      const userId = parseInt(userData?.userId, 10);
      const organizationId = parseInt(
        localStorage.getItem("organizationId"),
        10
      );

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      setLoadingModal(true);

      if (!invoice?.invoiceNumber) {
        setError("invalidInvoiceNumber");
        window.scroll(0, 0);
        return;
      }

      if (!selectedSupplier?.id) {
        setError("noSupplier");
        window.scroll(0, 0);
        return;
      }

      if (!selectedCustomer?.id) {
        setError("noCustomer");
        window.scroll(0, 0);
        return;
      }
      if (!selectedSubCompany?.id) {
        setError("noSubCompany");
        window.scroll(0, 0);
        return;
      }

      // Prepare the payload
      const payload = {
        supplierId: selectedSupplier?.id,
        customerId: selectedCustomer?.id,
        subCompanyId: selectedSubCompany?.id,
        createdBy: userId,
        ...invoice,
        amountForSupplier: sanitizeNumber(invoice?.amountForSupplier),
        abstractAmountForCustomer: sanitizeNumber(
          invoice?.abstractAmountForCustomer
        ),
        finalAmountForCustomer: sanitizeNumber(invoice?.finalAmountForCustomer),
        containers: inputs,
        organizationId,
      };

      setError("");
      // Send the request to the backend
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createMultiContainersInvoice`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
        }
      );

      const createdContainers = response?.data?.containers;

      // Handle the server response
      if (response.status === 200) {
        setShippedContainers(createdContainers);
        handleChildUploadDocs(response?.data?.invoice?.id);
        navigate("/invoicesOfShippedContainers");
        setError("");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message;
      if (errMsg === "alreadyLinkedToAnInvoice") {
        const containerNumber = error?.response?.data?.containerNumber;
        setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
      } else {
        setError(errMsg || "An error occurred");
      }
      window.scroll(0, 0);
    } finally {
      setLoadingModal(false);
    }
  };


  const handleAttemptCancel = () => {
    setShowAreYouSure(true);
  };

  const handleCancel = () => {
    setInputs([
      {
        containerNumber: "",
        expectedArrivalDate: new Date(),
        finalDestination: "",
        shipper: "",
        material: "",
        exitDate: new Date(),
        dateOfEntry: new Date(),
        shippingDate: new Date(),
        permitsReceivingDate: new Date(),
        amountForSupplier: "",
        abstractAmountForCustomer: "",
        finalAmountForCustomer: "",
        notes: "",
      },
    ]);
    navigate("/invoicesOfShippedContainers");
  };

  return (
    <>
      <div className="add-shipped-container-page">
        <h3 className={"add-shipped-container-title"}>{t("createInvoice")}</h3>

        {loadingModal ? <Loader /> : null}
        {error && !loadingModal && <ErrorBox>{t(error)}</ErrorBox>}

        <div className={` add-shipped-container-invoice-part`}>
          {!selectedSupplier?.id ||
          selectedSupplier?.id === "All" ||
          !selectedCustomer?.id ||
          selectedCustomer?.id === "All" ? (
            <InformationBox>
              {t("youHaveToSelectSupplierAndCustomer")}
            </InformationBox>
          ) : null}
          <div className="add-shipped-container-header">
            <section className="add-shipped-container-header-section">
              <span>{t("supplier")}: </span>
              <HotDropdown
                initOptions={suppliers}
                selected={
                  selectedSupplier.id && selectedSupplier.id !== "All"
                    ? selectedSupplier
                    : { id: "", name: `--${t("chooseASupplier")}--` }
                }
                setSelected={handleSelectSupplier}
                flex
                page="noRequest"
                getData={getSuppliers}
                errorText={error}
                optionsName={"suppliers"}
              />
            </section>
            <section className="add-shipped-container-header-section">
              <span>{t("customer")}: </span>
              <div className={classes.invoiceNumberContainer}>
                <HotDropdown
                  initOptions={customers}
                  selected={
                    selectedCustomer.id && selectedCustomer.id !== "All"
                      ? selectedCustomer
                      : { id: "", name: `--${t("chooseACustomer")}--` }
                  }
                  getData={getCustomers}
                  setSelected={handleSelectCustomer}
                  page="noRequest"
                  flex={true}
                  errorText={error}
                  optionsName={"customers"}
                />
              </div>
            </section>
          </div>

          <section className={classes.section}>
            <div className={classes.modalSubHeaderDivider}>
              <span className={classes.modalSubHeaderDividerFirstDiv}>
                {t("invoiceDetails")}:{" "}
              </span>
              <span className={classes.modalSubHeaderDividerSecondDiv}></span>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("subCompany")}: </span>
              <Dropdown
                options={subCompanies}
                selected={
                  selectedSubCompany !== null
                    ? selectedSubCompany
                    : { id: "", name: `--${t("chooseASubCompany")}--` }
                }
                // selected={selectedSubCompany}
                setSelected={handleSelectSubCompany}
                flex={true}
                getData={getSubCompanies}
                nameInAr={true}
              />
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("invoiceNumber")}: </span>
              <div className={classes.invoiceNumberContainer}>
                <CustomInput
                  name="invoiceNumber"
                  value={invoice?.invoiceNumber}
                  onChange={handleInvoiceInputChange}
                  flex={true}
                />
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("Date")}: </span>
              <CustomInput
                name="date"
                type="date"
                flex={true}
                value={
                  invoice?.date ? invoice?.date.toISOString().split("T")[0] : ""
                }
                onChange={(e) => handleInvoiceDateChange(e)}
              />
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("amountForSupplier")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="amountForSupplier"
                    value={invoice?.amountForSupplier}
                    onChange={handleAmountChange}
                    type="moneyV2"
                    flex={true}
                  />
                </div>
                <div>{selectedSupplier?.currency}</div>
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("currencyRate")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="currencyRate"
                    value={invoice?.currencyRate}
                    onChange={handleAmountChange}
                    type="moneyV2"
                    flex={true}
                  />
                </div>
                <div>{selectedCustomer?.currency}</div>
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("abstractAmountForCustomer")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="abstractAmountForCustomer"
                    value={invoice?.abstractAmountForCustomer}
                    onChange={handleAmountChange}
                    type="moneyV2"
                    flex={true}
                  />
                </div>
                <div>{selectedCustomer?.currency}</div>
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("received")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="received"
                    value={invoice?.received}
                    onChange={handleAmountChange}
                    flex={true}
                    type="moneyV2"
                  />
                </div>
                <div>{selectedCustomer?.currency}</div>
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("left")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="left"
                    value={invoice?.left}
                    onChange={handleAmountChange}
                    flex={true}
                    type="moneyV2"
                  />
                </div>
                <div>{selectedCustomer?.currency}</div>
              </div>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span className={classes.notesClass}>{t("notes")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="notes"
                    value={invoice?.notes}
                    onChange={handleInvoiceInputChange}
                    type="textarea"
                    flex={true}
                  />
                </div>
              </div>
            </div>
          </section>
          
          <PDFUploader
            ref={pdfUploaderRef}
            invoiceNumber={invoice?.invoiceNumber}
            invoiceDocs={invoiceDocs}
            setInvoiceDocs={setInvoiceDocs}
            mode="upload"
            documents={invoiceDocs}
            tempDocs={invoiceDocs}
            setTempDocs={setInvoiceDocs}
            invoiceType="multiContainersInvoice"
          />
        </div>

        {inputs?.length > 0 && (
          <>
            <section className={`${classes.section} ${classes.invoiceNumber}`}>
              <div className={classes.modalSubHeaderDivider}>
                <span className={classes.modalSubHeaderDividerFirstDiv}>
                  {t("containers")}:{" "}
                </span>
                <span className={classes.modalSubHeaderDividerSecondDiv}></span>
              </div>
            </section>

            <div className="all-containers u-styled-scrollbar">
              {inputs?.map((item, i) => {
                return (
                  <>
                    <ShippedContainerCard
                      key={i}
                      containerInfo={item}
                      containersLength={inputs.length}
                      index={i}
                      src={getRandomIcon()}
                      currentDirection={currentDirection}
                      withDelete={true}
                      handleAttemptDelete={(e) =>
                        handleAttemptDeleteContainer(e, i)
                      }
                    />
                  </>
                );
              })}
            </div>
          </>
        )}

        <section className="o-column o-column--vertical-align_end o-column--gap-size_500 u-margin--top_400">
          <CustomButton
            onClick={() => openModal(true)}
            variant="blank"
            title={
              inputs?.length > 1 ? t("addAnotherContainer") : t("addContainer")
            }
            icon={addIcon}
            iconSize={16}
            disabled={loadingModal}
          />
          <div className="o-row o-column--gap-size_500">
            <CustomButton
              title={t("cancel")}
              onClick={handleAttemptCancel}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              onClick={handleAddRow}
              title={t("confirm")}
              icon={saveIcon}
              iconSize={16}
              disabled={
                loadingModal ||
                !selectedSupplier?.id ||
                selectedSupplier?.id === "All" ||
                !selectedCustomer?.id ||
                selectedCustomer?.id === "All"
              }
            />
          </div>
        </section>
      </div>
      {showAddShippedContainerModal && (
        <AddShippedContainerModal
          inputs={inputs}
          setInputs={setInputs}
          onClose={() => setShowAddShippedContainerModal(false)}
          setShowAddShippedContainerModal={() =>
            setShowAddShippedContainerModal(false)
          }
        />
      )}

      {/* Cancel adding the Whole invoice: Are you sure modal */}
      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...invoice,
            ...inputs,
            ...invoiceDocs,
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancel}
            confirmIconType="whiteCheck"

        />
      ) : null}

      {/* Delete: Are you sure modal */}
      {areYouSureDelete && containerToDelete !== null ? (
        <AreYouSureModal
          onClose={() => {
            setAreYouSureDelete(false);
            setContainerToDelete(null);

          }}
          onConfirm={() => handleDeleteContainer(containerToDelete)}
          confirmIconType="whiteCheck"

        />
      ) : null}
    </>
  );
};

export default CreateInvoiceOfMultiContsPage;
